/**
 * Hedita Enterprise - Finance API Queries
 * Tabella: finance_transactions (admin_id)
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Transazione, TransazioneInsert, TransazioneUpdate,
  QueryOptions, FinanzeSummary 
} from '@/types';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

const TABLE = 'finance_transactions';

// ============================================================
// GET Queries
// ============================================================

export async function getTransazioni(options?: QueryOptions & { adminId?: string }) {
  const adminId = options?.adminId;
  if (!adminId) throw new Error('adminId required');
  
  let query = supabase
    .from(TABLE)
    .select('*', { count: options?.count })
    .eq('admin_id', adminId);

  // Apply filters
  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  } else {
    query = query.order('data', { ascending: false });
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;
  
  return { 
    data: data as Transazione[] | null, 
    error, 
    count 
  };
}

export async function getTransazione(id: string, adminId?: string) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('admin_id', adminId)
    .single();

  return {
    data: data as Transazione | null,
    error
  };
}

export async function getTransazioniByDateRange(
  dataInizio: Date, 
  dataFine: Date,
  adminId?: string
) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('admin_id', adminId)
    .gte('data', format(dataInizio, 'yyyy-MM-dd'))
    .lte('data', format(dataFine, 'yyyy-MM-dd'))
    .order('data', { ascending: false });

  return {
    data: data as Transazione[] | null,
    error
  };
}

// ============================================================
// MUTATIONS
// ============================================================

export async function createTransazione(transazione: TransazioneInsert, adminId?: string) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...transazione, admin_id: adminId })
    .select()
    .single();

  return {
    data: data as Transazione | null,
    error
  };
}

export async function updateTransazione(
  id: string, 
  updates: TransazioneUpdate,
  adminId?: string
) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('admin_id', adminId)
    .select()
    .single();

  return {
    data: data as Transazione | null,
    error
  };
}

export async function deleteTransazione(id: string, adminId?: string) {
  if (!adminId) throw new Error('adminId required');
  
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('admin_id', adminId);

  return { error };
}

// ============================================================
// SUMMARY & STATS
// ============================================================

export async function getFinanzeSummary(adminId?: string): Promise<{ summary: FinanzeSummary | null; error: any }> {
  if (!adminId) return { summary: null, error: new Error('adminId required') };
  
  const now = new Date();
  const monthStart = startOfMonth(now).toISOString().split('T')[0];
  const monthEnd = endOfMonth(now).toISOString().split('T')[0];
  const yearStart = startOfYear(now).toISOString().split('T')[0];
  const yearEnd = endOfYear(now).toISOString().split('T')[0];

  // Mese corrente
  const { data: meseData, error: meseError } = await supabase
    .from(TABLE)
    .select('tipo, importo')
    .eq('admin_id', adminId)
    .gte('data', monthStart)
    .lte('data', monthEnd);

  if (meseError) return { summary: null, error: meseError };

  // Anno corrente
  const { data: annoData, error: annoError } = await supabase
    .from(TABLE)
    .select('tipo, importo')
    .eq('admin_id', adminId)
    .gte('data', yearStart)
    .lte('data', yearEnd);

  if (annoError) return { summary: null, error: annoError };

  const calcolaTotali = (items: any[]) => {
    const entrate = items
      ?.filter(i => i.tipo === 'entrata')
      .reduce((sum, i) => sum + (i.importo || 0), 0) || 0;
    const uscite = items
      ?.filter(i => i.tipo === 'uscita')
      .reduce((sum, i) => sum + (i.importo || 0), 0) || 0;
    return { entrate, uscite, margine: entrate - uscite };
  };

  return {
    summary: {
      mese: calcolaTotali(meseData || []),
      anno: calcolaTotali(annoData || []),
    },
    error: null
  };
}

export async function getCategorieStats(adminId?: string) {
  if (!adminId) return { data: null, error: new Error('adminId required') };
  
  const yearStart = startOfYear(new Date()).toISOString().split('T')[0];
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('categoria, tipo, importo')
    .eq('admin_id', adminId)
    .gte('data', yearStart);

  if (error || !data) {
    return { data: null, error };
  }

  const stats = data.reduce((acc, item) => {
    const key = item.categoria || 'Altro';
    if (!acc[key]) {
      acc[key] = { categoria: key, entrate: 0, uscite: 0, totale: 0 };
    }
    if (item.tipo === 'entrata') {
      acc[key].entrate += item.importo || 0;
      acc[key].totale += item.importo || 0;
    } else {
      acc[key].uscite += item.importo || 0;
      acc[key].totale -= item.importo || 0;
    }
    return acc;
  }, {} as Record<string, { categoria: string; entrate: number; uscite: number; totale: number }>);

  return {
    data: Object.values(stats).sort((a, b) => b.totale - a.totale),
    error: null
  };
}

export async function getTransazioniInPeriodo(
  startDate: string, 
  endDate: string, 
  adminId?: string
) {
  if (!adminId) return { data: null, error: new Error('adminId required') };
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('admin_id', adminId)
    .gte('data', startDate)
    .lte('data', endDate)
    .order('data', { ascending: false });

  return { data, error };
}

export async function getTransazioneById(id: string, adminId?: string) {
  if (!adminId) return { data: null, error: new Error('adminId required') };
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('admin_id', adminId)
    .single();

  return { data, error };
}

export async function getTrendMensile(months: number = 6, adminId?: string) {
  if (!adminId) return { data: null, error: new Error('adminId required') };
  
  const trends = [];
  const now = new Date();
  
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthStart = startOfMonth(d).toISOString().split('T')[0];
    const monthEnd = endOfMonth(d).toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from(TABLE)
      .select('tipo, importo')
      .eq('admin_id', adminId)
      .gte('data', monthStart)
      .lte('data', monthEnd);
    
    if (error) continue;
    
    const entrate = data?.filter(t => t.tipo === 'entrata').reduce((a, t) => a + (t.importo || 0), 0) || 0;
    const uscite = data?.filter(t => t.tipo === 'uscita').reduce((a, t) => a + (t.importo || 0), 0) || 0;
    
    trends.push({
      mese: format(d, 'MMM yyyy'),
      entrate,
      uscite,
      margine: entrate - uscite
    });
  }
  
  return { data: trends, error: null };
}
